<template>
    <TDataTable
        class="project-list-table"
        data-testid="project-list-table"
        :value="tableData"
        responsive-layout="scroll"
        scrollable
        :loading="loading"
        :group-rows-by="''"
        row-group-mode="subheader"
        :class="{isScrollingX, isScrollingY}"
    >
        <TColumn
            v-for="column in columns"
            :key="column"
            :field="column"
            class="data-table-column"
            :class="[getTableSize, getColumnWidthClass(column)]"
            :frozen="column === 'actions'"
            align-frozen="right"
        >
            <template #header>
                <div
                    :data-testid="`project-list-table-th-${column}`"
                    class="data-table-column_header"
                    :class="[{isSelected: sortingOptions?.sortBy === column}, `${column}-column`]"
                    @click="handleSort(column)"
                >
                    <div class="data-table-column_header_wrapper">
                        <span class="data-table-column_header_label">
                            {{ t(`projectclient.projectlist.table.${column}.header`) }}
                        </span>
                        <VJoyIcon
                            v-if="sortingOptions?.sortBy === column"
                            class="data-table-column_header_icon"
                            :name="getSortIcon(column)"
                            size="xsmall"
                        />
                    </div>
                    <VJoyTooltip v-if="column === 'total'" variant="secondary" class="joy-ml-1">
                        <VJoyIcon name="question-circle" size="xsmall" />
                        <template #tooltip-content>
                            {{ t('projectclient.projectlist.table.total.header.tooltip') }}
                        </template>
                    </VJoyTooltip>
                </div>
            </template>
            <template #body="{data, index}">
                <TableCellWrapper :key="data.id" :column="column" :data="data" :index="index" />
            </template>
        </TColumn>

        <template v-if="!isFullProjectListLoaded" #groupfooter>
            <div class="project-list-table_infinite-scroll">
                <InfiniteScrollWitness key="scrollWitness" ref="witnessRef" @mount="startObserving">
                    <VJoySpinner />
                </InfiniteScrollWitness>
            </div>
        </template>
        <template #footer>
            <span>{{ t('projectclient.projectlist.table.search.footer.total') }} {{ totalElements }}</span>
        </template>
        <template #loading>
            <VJoySpinner />
        </template>
        <template #empty>
            <p>{{ t('projectclient.projectlist.table.search.empty') }}</p>
        </template>
    </TDataTable>
</template>

<script setup lang="ts">
    import {watch, computed, onBeforeUnmount, onMounted, ref} from 'vue';
    import {useUserStore} from '@common/stores';
    import {type DataTableColumn, useProjectsListStore} from '@features';
    import {storeToRefs} from 'pinia';
    import {nextTick, useTranslation} from '#imports';
    import {createDataTableComponents} from '@malt/joy-entrypoint/src/vue/utils';
    import {useInfiniteScroll, useScrollContainer} from '@malt/utils';
    import {VJoyIcon, VJoySpinner, VJoyTooltip} from '@maltjoy/core-vue';
    import {SortType, SortOrder} from '@api';
    import TableCellWrapper from './TableCellWrapper.vue';
    import type {DataTableColumnArray, DataTableSize, FormattedClientProjectResource, SortingOptions} from '../../types';
    import {mapToSortType} from '../../utils';

    const {TColumn, TDataTable} = createDataTableComponents<FormattedClientProjectResource>();

    const {t} = useTranslation();

    interface Props {
        tableData: FormattedClientProjectResource[];
        totalElements: number;
        size?: DataTableSize;
        sortingOptions: SortingOptions;
    }

    const props = withDefaults(defineProps<Props>(), {
        size: 'large',
    });

    const userStore = useUserStore();
    const {isPlatformAdmin, isPOMandatory} = storeToRefs(userStore);

    const projectListStore = useProjectsListStore();
    const {loading, isAdminOfAClientCompany, hasMultipleClientCompanies} = storeToRefs(projectListStore);

    const isFullProjectListLoaded = computed(() => {
        return props.tableData?.length === props.totalElements;
    });

    watch(loading, async () => {
        if (!loading.value) {
            await nextTick();
            datatableWrapperRef.value?.scrollTo({top: 0});
        }
    });

    const datatableWrapperRef = ref<HTMLElement>();

    const {isScrollingX, isScrollingY, startListening, stopListening, containerWidth} = useScrollContainer({
        target: datatableWrapperRef,
        immediate: false,
    });

    const {InfiniteScrollWitness, witnessRef, isIntersecting, startObserving, stopObserving} = useInfiniteScroll(
        (event) => {
            if (isIntersecting.value && !projectListStore.loadingMore) {
                projectListStore.loadMoreProjects();
            }
        },
        {immediate: false},
    );

    function getSortIcon(column: keyof FormattedClientProjectResource) {
        if (props.sortingOptions?.sortBy === column && props.sortingOptions.sortOrder === SortOrder.Asc) {
            return 'sort-asc';
        } else {
            return 'sort-desc';
        }
    }

    const getTableSize = computed(() => (props.size === 'large' ? 'large-size' : props.size === 'medium' ? 'medium-size' : 'small-size'));

    function getColumnWidthClass(columnName: keyof FormattedClientProjectResource): string {
        return `${columnName}-column`;
    }

    const columns = computed<DataTableColumnArray>(() => {
        const baseColumns: (DataTableColumn | undefined)[] = [
            'title',
            'status',
            'freelancer',
            'total',
            ...(isPlatformAdmin.value ? (['organization', 'freelancerPrice'] as const) : []),
            isPlatformAdmin.value || hasMultipleClientCompanies.value ? 'company' : undefined,
            isPlatformAdmin.value || isAdminOfAClientCompany.value ? 'projectOwner' : undefined,
            ...(isPlatformAdmin.value ? (['verticalType', 'opportunityType'] as const) : []),
            isPlatformAdmin.value || isPOMandatory.value ? 'purchaseOrder' : undefined,
            'startDate',
            'endDate',
            ...(isPlatformAdmin.value ? (['renewalStatus'] as const) : []),
            'sortDateForClient',
            'actions',
        ];

        return baseColumns.filter((column): column is DataTableColumn => !!column);
    });

    function handleSort(column: DataTableColumn) {
        const newSortBy = column;

        if (props.sortingOptions?.sortBy === newSortBy) {
            if (props.sortingOptions?.sortOrder === SortOrder.Asc) {
                projectListStore.searchParams.sortOrder = SortOrder.Desc;
            } else {
                projectListStore.searchParams.sortBy = SortType.Action;
                projectListStore.searchParams.sortOrder = SortOrder.Asc;
            }
        } else {
            projectListStore.searchParams.sortBy = mapToSortType(newSortBy);
            projectListStore.searchParams.sortOrder = SortOrder.Asc;
        }
    }

    async function startObservingScrollAndInfinite() {
        await nextTick();
        datatableWrapperRef.value = document.querySelector('.p-datatable-table-container') as HTMLElement;
        startListening();
        startObserving();
    }

    onMounted(() => {
        startObservingScrollAndInfinite();
    });

    onBeforeUnmount(() => {
        stopListening();
        stopObserving();
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .project-list-table {
        width: 100%;
        height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        font-size: var(--joy-font-size-primary-300);
        overflow: auto;

        :deep(.p-datatable-table-container) {
            height: 100%;
        }

        :deep(tr:hover) {
            background-color: var(--joy-color-neutral-20);
        }

        :deep(th.data-table-column),
        :deep(td.data-table-column) {
            white-space: nowrap;
            text-overflow: ellipsis;
            min-width: 120px;
            padding: 0;

            &.large-size {
                height: 56px;
            }

            &.medium-size {
                height: 48px;
            }

            &.small-size {
                height: 40px;
            }

            &.title-column {
                width: 200px;
                max-width: 280px;
            }

            &.actions-column {
                min-width: 150px;
            }
        }

        :deep(th.data-table-column) {
            border-bottom: 1px solid var(--joy-color-neutral-30);
            border-top: 1px solid var(--joy-color-neutral-30);
            background-color: var(--joy-color-neutral-10);
            padding: 0 var(--joy-core-spacing-4);
        }

        :deep(.p-datatable-column-header-content) {
            width: 100%;
        }

        :deep(.data-table-column_header) {
            display: flex;
            min-width: 0;
            flex-flow: row nowrap;
            align-items: center;
            width: 100%;

            &.startDate-column,
            &.endDate-column,
            &.sortDateForClient-column,
            &.total-column {
                justify-content: flex-end;
            }

            &.total-column {
                .joy-tooltip__wrapper {
                    display: flex;
                    align-items: center;

                    .joy-icon {
                        color: var(--joy-color-text-default);
                    }
                }
            }
        }

        .data-table-column {
            &_link {
                text-decoration: none;
                color: inherit;
                cursor: pointer;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
            }

            &_header {
                color: var(--joy-color-text-default);
                font-weight: var(--joy-font-weight-normal);
                font-size: var(--joy-font-size-primary-300);
                line-height: var(--joy-line-height-large);
                letter-spacing: var(--joy-font-letter-spacing-tight);
                border-bottom: 0.7px solid transparent;

                &:not(.isSelected) > span:hover,
                &:not(.isSelected) > span:focus {
                    border-bottom: 0.7px solid var(--joy-color-neutral-50);
                }

                &_label {
                    @include text.getFontBySize(primary-350);

                    color: var(--joy-color-text-default);
                }

                &_icon {
                    color: var(--neutral-joy-color-neutral-50);
                }

                &_wrapper {
                    display: flex;
                    cursor: pointer;

                    &:hover,
                    &:focus {
                        border-bottom: 0.7px solid var(--joy-color-neutral-50);
                    }
                }
            }
        }

        :deep(.p-datatable-loading-overlay) {
            height: 100%;
            width: 100%;
            background-color: rgb(0 0 0 / 20%);
        }

        :deep(.p-datatable-emptymessage > td) {
            text-align: center;
            height: 56px;

            & > p {
                width: 100%;
            }
        }

        :deep(.p-datatable-footer) {
            background: var(--joy-color-neutral-10);
            border-top: 1px solid var(--joy-color-neutral-30) !important;
            color: var(--neutral-joy-color-neutral-50);
            font-weight: var(--joy-font-weight-normal);
        }

        &_infinite-scroll {
            min-height: 40px !important;
            padding: var(--joy-core-spacing-4) 0;
        }

        &.isScrollingX {
            :deep(td.data-table-column .actions-column) {
                box-shadow:
                    0 0 0 0 var(--joy-color-neutral-0),
                    0 0 0 0 var(--joy-color-neutral-0),
                    0 0 0 0 var(--joy-color-neutral-30),
                    -3px 0 7px 0 var(--joy-color-neutral-30);
            }
        }

        &.isScrollingY {
            :deep(thead.p-datatable-thead) {
                box-shadow: -3px 0 12px 0 var(--joy-color-neutral-30);
            }

            :deep(tr:last-child td.data-table-column) {
                border-bottom: none;
            }
        }
    }
</style>
